import { HttpErrorResponse } from "@angular/common/http";
import { ErrorHandler, Injectable, Injector, inject } from "@angular/core";
import { CrashlyticsService } from "app/core/crashlytics/crashlytics.service";

/**
 * HTTP failures that are expected noise rather than defects:
 *
 *   0   - device offline / request aborted; a connectivity fact, not a bug
 *   401 - handled by the auth interceptor, which redirects to sign-in
 *   403 - the server correctly refusing an action the user may not perform
 *
 * Reporting these would bury real defects under thousands of events. Everything
 * else, including every 5xx, is reported.
 */
const IGNORED_HTTP_STATUSES = new Set([0, 401, 403]);

/**
 * Catches every error Angular does not handle and forwards it to Crashlytics
 * as a non-fatal report.
 *
 * Without this, a WebView app reports almost nothing: a JavaScript exception
 * does not crash the Android process, so native Crashlytics never sees it.
 *
 * CrashlyticsService is resolved lazily through the Injector rather than
 * injected directly. An ErrorHandler is constructed before the rest of the
 * injector is ready, and pulling in a service whose own dependency graph
 * reaches HttpClient at construction time causes a circular dependency.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private _injector = inject(Injector);

  handleError(error: unknown): void {
    // Preserve the default behaviour first, so local debugging is unchanged
    // and nothing is lost if the report below fails.
    console.error(error);

    if (this._shouldIgnore(error)) {
      return;
    }

    try {
      const crashlytics = this._injector.get(CrashlyticsService);

      void crashlytics.recordException(error, "Uncaught Angular error");
    } catch (reportingError) {
      // Never let the error handler throw -- that would replace a recoverable application error with an unrecoverable one.
      console.warn("[Crashlytics] failed to report error", reportingError);
    }
  }

  private _shouldIgnore(error: unknown): boolean {
    return error instanceof HttpErrorResponse && IGNORED_HTTP_STATUSES.has(error.status);
  }
}
